import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash';
import { useSyncExternalStore } from 'react';

import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { type AllFlagsType, flagDefinitions } from './flags';
import { getResolvedAllFlags } from './utils';

let clientInitialized = false;

const cachedFlagsRef: { current: AllFlagsType } = {
  current: getResolvedAllFlags({}),
};

let _cacheSubscriptions = new Set<(allFlags: AllFlagsType) => void>();
const subscribeToFlagCache = (fn: (allFlags: AllFlagsType) => void) => {
  _cacheSubscriptions.add(fn);

  return () => {
    _cacheSubscriptions.delete(fn);
  };
};

// This preInit function is needed because of the way the LocalStorage wrapper
// syncs asynchronously on native. If we called LocalStorage.getItem immediately,
// it is not guaranteed that the storage has already synced.
(async function preInit() {
  await LocalStorage.sync();
  const nextAllFlags = getResolvedAllFlags(
    LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_FLAGS) ?? {}
  );

  if (!clientInitialized) {
    cachedFlagsRef.current = nextAllFlags;
    _cacheSubscriptions.forEach(fn => fn(nextAllFlags));
  }
})();

export const getAllFlagsSync = () => cachedFlagsRef.current;

/** Whether flags have arrived from LaunchDarkly, i.e. `useFlag` is past its fallbacks. */
export const isClientInitializedSync = () => clientInitialized;

export const getFlagSync = <TFlagDefinitionKey extends keyof AllFlagsType>(
  definitionKey: TFlagDefinitionKey
): AllFlagsType[TFlagDefinitionKey] | undefined => {
  return getAllFlagsSync()[definitionKey];
};

export const handleFlagsInitOrChange = (flagSet: LDFlagSet) => {
  clientInitialized = true;
  const nextAllFlags = getResolvedAllFlags(flagSet);

  if (!isEqual(cachedFlagsRef.current, nextAllFlags)) {
    cachedFlagsRef.current = nextAllFlags;
    LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_FLAGS, flagSet);
    _cacheSubscriptions.forEach(fn => fn(nextAllFlags));
  }
};

export function useAllFlags() {
  return useSyncExternalStore(subscribeToFlagCache, getAllFlagsSync);
}

/**
 * Subscribe to a LaunchDarkly flag value from the shared client.
 */
export function useFlag<T extends keyof typeof flagDefinitions>(
  definitionKey: T,
  defaultValue?: (typeof flagDefinitions)[T]['defaultValue']
): (typeof flagDefinitions)[T]['defaultValue'] {
  const allFlags = useAllFlags();
  const definition = flagDefinitions[definitionKey];
  // If no defaultValue is provided to the hook we rely on the value from the flag definition
  const fallbackValue =
    typeof defaultValue === 'undefined' ? definition.defaultValue : defaultValue;

  return clientInitialized ? allFlags[definitionKey] : fallbackValue;
}
